import React, { useEffect, useState, useRef } from 'react';
import { useOrganization } from '../../context/OrganizationContext';
import { collection, doc, query, orderBy, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { validarArchivoCreativo } from './creativeConstraints';
import { db, functions, storage } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

const WIZARD_STEPS = ['content', 'creatives', 'targeting', 'budget', 'review'];
const PLATFORMS = [
  { id: 'meta', label: 'Meta Ads', icon: '📘' },
  { id: 'tiktok', label: 'TikTok Ads', icon: '🎵' },
  { id: 'google', label: 'Google Ads', icon: '🔍' }
];

export default function CreateCampaign({ initialData }) {
  const { context } = useOrganization();
  const { t } = useTranslation('adsStudio');
  const [step, setStep] = useState(0);

  // Step 1: Content
  const [campaignName, setCampaignName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState(['meta']);
  const [contentLanguage, setContentLanguage] = useState('es');
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generatingCopy, setGeneratingCopy] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);

  // Step 2: Creatives
  const [creatives, setCreatives] = useState([]);
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [creativeMode, setCreativeMode] = useState('library'); // library, generate, upload
  const [genImagePrompt, setGenImagePrompt] = useState('');
  const [genAspectRatio, setGenAspectRatio] = useState('1:1');
  const [generatingImage, setGeneratingImage] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [arrastrando, setArrastrando] = useState(false);
  const fileInputRef = useRef(null);

  // Step 3: Targeting
  const [targetCountries, setTargetCountries] = useState([]);
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(65);

  // Step 4: Budget
  const [dailyBudget, setDailyBudget] = useState(5);
  const [currency, setCurrency] = useState('USD');

  // Step 5: Review & Publish
  const [platformConfigs, setPlatformConfigs] = useState({});
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  // Draft save
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const uid = getAuth().currentUser?.uid;
  const orgId = context.isOrganization ? context.orgId : null;
  const basePath = context.isOrganization ? `organizations/${context.orgId}` : `clientes/${uid}`;

  // Pre-fill from insight opportunity
  useEffect(() => {
    if (initialData?.opportunity) {
      setPrompt(initialData.opportunity);
    }
  }, [initialData]);

  // Load platform configs for review step
  useEffect(() => {
    if (!uid) return;
    const unsubs = ['meta', 'tiktok', 'google'].map(p => {
      const ref = doc(db, basePath, 'adsConfig', p);
      return onSnapshot(ref, (snap) => {
        setPlatformConfigs(prev => ({ ...prev, [p]: snap.exists() ? snap.data() : null }));
      }, () => {});
    });
    return () => unsubs.forEach(u => u());
  }, [uid, basePath]);

  // Load creatives for step 2
  useEffect(() => {
    if (!uid) return;
    const q = query(collection(db, basePath, 'adsCreatives'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setCreatives(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [uid, basePath]);

  const togglePlatform = (id) => {
    setSelectedPlatforms(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleGenerateCopy = async () => {
    setGeneratingCopy(true);
    try {
      const call = httpsCallable(functions, 'generateAdContent', { timeout: 60000 });
      const result = await call({
        organizationId: orgId,
        prompt,
        insightRef: initialData?.weekId || null,
        opportunity: initialData?.opportunity || null,
        language: contentLanguage,
        platforms: selectedPlatforms
      });
      setGeneratedContent(result.data?.content || null);
    } catch (err) {
      console.error('Generate copy error:', err);
      alert(err.message);
    } finally {
      setGeneratingCopy(false);
    }
  };

  const updateContent = (platform, field, value) => {
    setGeneratedContent(prev => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value }
    }));
  };

  const handleGenerateImage = async () => {
    setGeneratingImage(true);
    try {
      const call = httpsCallable(functions, 'generateAdMedia', { timeout: 120000 });
      const result = await call({ organizationId: orgId, prompt: genImagePrompt, aspectRatio: genAspectRatio });
      if (result.data?.creativeId) {
        setSelectedCreative({ id: result.data.creativeId, url: result.data.url, type: 'image', source: 'ai' });
      }
    } catch (err) {
      console.error('Generate image error:', err);
      alert(err.message);
    } finally {
      setGeneratingImage(false);
    }
  };

  /**
   * Camino ÚNICO de subida: lo llaman tanto el input como el drop.
   *
   * Se extrajo del handler del input porque un drop trae el archivo en
   * `e.dataTransfer.files` y no en `e.target.files`: sin esto habría que duplicar
   * validación y subida, y mañana una de las dos copias se quedaría atrás.
   *
   * Devuelve si el archivo fue ACEPTADO, que es lo que el camino del input necesita
   * para decidir si limpia el `value`.
   */
  const procesarArchivo = async (file) => {
    if (!file) return false;

    // El `accept` del input NO valida: es un filtro del selector de archivos. Y en un
    // DROP no se aplica en absoluto — cualquier fichero llega aquí. Por eso la
    // validación vive en este camino común y no en el handler del input.
    const validacion = validarArchivoCreativo(file);
    if (!validacion.ok) {
      alert(validacion.motivo === 'tamano'
        ? t('creatives.upload.maxSize')
        : t('creatives.upload.error'));
      return false;
    }

    setUploading(true);
    setUploadProgress(0);

    const entityId = orgId || uid;
    const ext = file.name.split('.').pop();
    const storageRef = ref(storage, `ads-creatives/${entityId}/${Date.now()}.${ext}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on('state_changed',
      (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      () => { setUploading(false); alert(t('creatives.upload.error')); },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        // Antes: `file.type.startsWith('image/')`, que etiquetaba como imagen cualquier
        // image/* —webp, gif, svg— aunque no esté en ACCEPTED_TYPES. Ahora el tipo sale
        // de la misma validación que decidió aceptarlo.
        const isImage = validacion.tipo === 'image';
        try {
          const call = httpsCallable(functions, 'uploadCreative');
          const result = await call({ organizationId: orgId, url, type: isImage ? 'image' : 'video', metadata: { sizeBytes: file.size } });
          setSelectedCreative({ id: result.data?.creativeId, url, type: isImage ? 'image' : 'video', source: 'upload' });
        } catch (err) { console.error(err); }
        setUploading(false);
        setUploadProgress(0);
      }
    );
    return true;
  };

  const handleUpload = async (e) => {
    // Se guarda el nodo ANTES del await: después, `e.target` puede no servir.
    const input = e.target;
    const aceptado = await procesarArchivo(input.files?.[0]);
    // Sólo al RECHAZAR, igual que antes: sin limpiar el value, volver a elegir el
    // MISMO archivo no dispara onChange y la subida parece colgada. En el camino del
    // drop no aplica — ahí no hay input cuyo value quede pegado.
    if (!aceptado) input.value = '';
  };

  // Sin preventDefault en dragOver el navegador se queda el archivo y lo abre en una
  // pestaña: onDrop NO llega a dispararse. Es la razón por la que el arrastre no
  // funcionaba, además de que no había handlers.
  const handleDragOver = (e) => e.preventDefault();

  // dragenter/dragleave se disparan TAMBIÉN al pasar sobre los hijos de la zona (el
  // icono, los dos <p>) y burbujean hasta aquí, así que un simple set(true)/set(false)
  // hace parpadear el realce al mover el ratón dentro de la propia zona.
  // Se lleva la cuenta de entradas menos salidas: mientras sea > 0 el puntero sigue
  // dentro. Se descartó la alternativa de mirar `relatedTarget`, que además de fallar
  // en navegadores cuando el hijo se desmonta a mitad de arrastre, no es observable en
  // jsdom — habría dejado este comportamiento sin test.
  const profundidadArrastre = useRef(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    profundidadArrastre.current += 1;
    setArrastrando(true);
  };

  const handleDragLeave = () => {
    profundidadArrastre.current = Math.max(0, profundidadArrastre.current - 1);
    if (profundidadArrastre.current === 0) setArrastrando(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    profundidadArrastre.current = 0;
    setArrastrando(false);
    // Sólo el primero, igual que el selector de archivos: el texto de la zona dice
    // "Arrastra un archivo" en los tres idiomas y no promete varios.
    procesarArchivo(e.dataTransfer.files?.[0]);
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const creative = {};
      const headline = {};
      const body = {};
      const cta = {};
      if (generatedContent) {
        for (const p of selectedPlatforms) {
          if (generatedContent[p]) {
            headline[p] = generatedContent[p].headline || '';
            body[p] = generatedContent[p].body || '';
            cta[p] = generatedContent[p].cta || '';
          }
        }
      }
      creative.headline = headline;
      creative.body = body;
      creative.cta = cta;
      creative.mediaUrl = selectedCreative?.url || null;
      creative.mediaType = selectedCreative?.type || null;
      creative.mediaSource = selectedCreative?.source || null;

      await addDoc(collection(db, basePath, 'adsCampaigns'), {
        title: campaignName || 'Untitled Campaign',
        status: 'draft',
        platforms: selectedPlatforms,
        creative,
        platformCampaigns: { meta: null, tiktok: null, google: null },
        targeting: targetCountries.length > 0 ? {
          locations: targetCountries,
          ageMin,
          ageMax
        } : null,
        budget: dailyBudget > 0 ? {
          type: 'daily',
          amount: dailyBudget,
          currency
        } : null,
        performance: { impressions: 0, clicks: 0, spend: 0, conversions: 0, lastSyncAt: null },
        insightRef: initialData?.weekId || null,
        createdAt: serverTimestamp(),
        createdBy: uid,
        publishedAt: null,
        organizationId: orgId
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Save draft error:', err);
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm(t('review.publishConfirm'))) return;
    setPublishing(true);
    setPublishResult(null);
    try {
      // First save as draft
      await handleSaveDraft();
      // Then publish — but we need the campaign ID. For now, save draft and use the most recent one.
      // A better flow would return the ID from saveDraft. Let's publish via the last saved.
      const call = httpsCallable(functions, 'publishCampaign', { timeout: 120000 });
      // We need campaign ID — get it from the most recent draft
      const q = query(collection(db, basePath, 'adsCampaigns'), orderBy('createdAt', 'desc'));
      const snap = await new Promise((resolve) => {
        const unsub = onSnapshot(q, (s) => { unsub(); resolve(s); });
      });
      const latestCampaign = snap.docs[0];
      if (!latestCampaign) throw new Error('No campaign found');

      const result = await call({ organizationId: orgId, campaignId: latestCampaign.id });
      setPublishResult(result.data);
    } catch (err) {
      console.error('Publish error:', err);
      setPublishResult({ ok: false, error: err.message });
    } finally {
      setPublishing(false);
    }
  };

  const canProceedStep1 = generatedContent && selectedPlatforms.some(p => generatedContent[p]);
  const canProceedStep2 = !!selectedCreative;
  const canProceedStep3 = targetCountries.length > 0;
  const canProceedStep4 = dailyBudget >= 1;
  const currentStep = WIZARD_STEPS[step];

  return (
    <div>
      <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.3rem' }}>{t('createCampaign.title')}</h2>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem' }}>
        {WIZARD_STEPS.map((s, i) => (
          <div key={s} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              height: '4px', borderRadius: '2px', marginBottom: '0.5rem',
              backgroundColor: i <= step ? '#1a73e8' : '#e0e0e0'
            }} />
            <span style={{ fontSize: '0.75rem', color: i <= step ? '#1a73e8' : '#999' }}>
              {t(`createCampaign.steps.${s}`)}
            </span>
          </div>
        ))}
      </div>

      {/* Step 1: Content */}
      {currentStep === 'content' && (
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
              {t('createCampaign.content.name')}
            </label>
            <input
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder={t('createCampaign.content.namePlaceholder')}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.9rem', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
              {t('createCampaign.content.prompt')}
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t('createCampaign.content.promptPlaceholder')}
              rows={3}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
              {t('createCampaign.content.platforms')}
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {PLATFORMS.map(({ id, label, icon }) => (
                <button key={id} onClick={() => togglePlatform(id)} style={{
                  padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer',
                  border: selectedPlatforms.includes(id) ? '2px solid #1a73e8' : '1px solid #ddd',
                  backgroundColor: selectedPlatforms.includes(id) ? '#e8f0fe' : '#fff',
                  color: selectedPlatforms.includes(id) ? '#1a73e8' : '#333'
                }}>
                  {icon} {label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
              {t('createCampaign.content.language')}
            </label>
            <select value={contentLanguage} onChange={(e) => setContentLanguage(e.target.value)} style={{
              padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.9rem'
            }}>
              <option value="es">Español</option>
              <option value="en">English</option>
              <option value="pt">Português</option>
            </select>
          </div>

          <button
            onClick={handleGenerateCopy}
            disabled={!prompt.trim() || selectedPlatforms.length === 0 || generatingCopy}
            style={{
              padding: '0.75rem 1.5rem', backgroundColor: (!prompt.trim() || generatingCopy) ? '#ccc' : '#1a73e8', color: '#fff',
              border: 'none', borderRadius: '8px', cursor: (!prompt.trim() || generatingCopy) ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem', fontWeight: '600', marginBottom: '1rem'
            }}
          >
            {generatingCopy ? t('createCampaign.content.generatingCopy') : t('createCampaign.content.generateCopy')}
          </button>

          {/* Generated copy preview */}
          {generatedContent && (
            <div>
              <h3 style={{ fontSize: '1rem', margin: '1rem 0 0.75rem 0' }}>{t('createCampaign.content.preview')}</h3>
              {selectedPlatforms.map((p) => {
                const c = generatedContent[p];
                if (!c) return null;
                return (
                  <div key={p} style={{
                    padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '0.75rem', border: '1px solid #e8e8e8'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <strong style={{ fontSize: '0.9rem' }}>{PLATFORMS.find(pl => pl.id === p)?.icon} {p.charAt(0).toUpperCase() + p.slice(1)}</strong>
                      <button onClick={() => setEditingPlatform(editingPlatform === p ? null : p)} style={{
                        background: 'none', border: 'none', color: '#1a73e8', cursor: 'pointer', fontSize: '0.8rem'
                      }}>
                        {t('createCampaign.content.edit')}
                      </button>
                    </div>
                    {editingPlatform === p ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <input value={c.headline} onChange={(e) => updateContent(p, 'headline', e.target.value)}
                          style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem' }} />
                        <textarea value={c.body} onChange={(e) => updateContent(p, 'body', e.target.value)} rows={2}
                          style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem', resize: 'vertical' }} />
                        <input value={c.cta} onChange={(e) => updateContent(p, 'cta', e.target.value)}
                          style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem' }} />
                      </div>
                    ) : (
                      <>
                        <p style={{ margin: '0 0 0.25rem 0', fontWeight: '600', fontSize: '0.9rem' }}>{c.headline}</p>
                        <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.85rem', color: '#444' }}>{c.body}</p>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#1a73e8' }}>{c.cta}</p>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Creatives */}
      {currentStep === 'creatives' && (
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>{t('createCampaign.selectCreative.title')}</h3>

          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            {[['library', 'fromLibrary'], ['generate', 'generateNew'], ['upload', 'uploadNew']].map(([mode, key]) => (
              <button key={mode} onClick={() => setCreativeMode(mode)} style={{
                padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer',
                border: creativeMode === mode ? '2px solid #1a73e8' : '1px solid #ddd',
                backgroundColor: creativeMode === mode ? '#e8f0fe' : '#fff',
                color: creativeMode === mode ? '#1a73e8' : '#333'
              }}>
                {t(`createCampaign.selectCreative.${key}`)}
              </button>
            ))}
          </div>

          {/* Library */}
          {creativeMode === 'library' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.75rem' }}>
              {creatives.map((c) => (
                <div key={c.id} onClick={() => setSelectedCreative(c)} style={{
                  borderRadius: '8px', overflow: 'hidden', cursor: 'pointer',
                  border: selectedCreative?.id === c.id ? '3px solid #1a73e8' : '2px solid transparent',
                  position: 'relative'
                }}>
                  <img src={c.url} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
                  {selectedCreative?.id === c.id && (
                    <div style={{
                      position: 'absolute', top: '4px', right: '4px', backgroundColor: '#1a73e8', color: '#fff',
                      borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem'
                    }}>&#10003;</div>
                  )}
                </div>
              ))}
              {creatives.length === 0 && (
                <p style={{ gridColumn: '1/-1', textAlign: 'center', color: '#888', fontSize: '0.9rem', padding: '2rem 0' }}>
                  {t('creatives.empty.description')}
                </p>
              )}
            </div>
          )}

          {/* Generate */}
          {creativeMode === 'generate' && (
            <div>
              <textarea
                value={genImagePrompt || (generatedContent?.[selectedPlatforms[0]]?.imagePrompt || '')}
                onChange={(e) => setGenImagePrompt(e.target.value)}
                placeholder={t('creatives.generateModal.promptPlaceholder')}
                rows={3}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', marginBottom: '0.75rem' }}
              />
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                {[['1:1', 'square'], ['9:16', 'portrait'], ['16:9', 'landscape']].map(([ratio, key]) => (
                  <button key={ratio} onClick={() => setGenAspectRatio(ratio)} style={{
                    flex: 1, padding: '0.4rem', borderRadius: '6px', fontSize: '0.8rem',
                    border: genAspectRatio === ratio ? '2px solid #1a73e8' : '1px solid #ddd',
                    backgroundColor: genAspectRatio === ratio ? '#e8f0fe' : '#fff', cursor: 'pointer'
                  }}>
                    {t(`creatives.generateModal.${key}`)}
                  </button>
                ))}
              </div>
              <button onClick={handleGenerateImage} disabled={generatingImage} style={{
                padding: '0.5rem 1rem', backgroundColor: generatingImage ? '#ccc' : '#28a745', color: '#fff',
                border: 'none', borderRadius: '8px', cursor: generatingImage ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: '600'
              }}>
                {generatingImage ? t('creatives.generateModal.generating') : t('creatives.generateModal.generateImage')}
              </button>
            </div>
          )}

          {/* Upload */}
          {creativeMode === 'upload' && (
            <div>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  padding: '3rem', textAlign: 'center', borderRadius: '12px', cursor: 'pointer',
                  // El realce no es cosmético: sin él no hay forma de saber que la zona
                  // acepta el archivo, y el usuario suelta a ciegas.
                  border: `2px dashed ${arrastrando ? '#1a73e8' : '#ddd'}`,
                  backgroundColor: arrastrando ? '#eef4fd' : '#fafafa',
                  transition: 'border-color 0.15s, background-color 0.15s',
                }}
              >
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📁</div>
                <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.9rem' }}>{t('creatives.upload.dragDrop')}</p>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>{t('creatives.upload.formats')} — {t('creatives.upload.maxSize')}</p>
              </div>
              <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.mp4,.mov" onChange={handleUpload} style={{ display: 'none' }} />
              {uploading && (
                <div style={{ marginTop: '0.75rem', height: '4px', backgroundColor: '#e0e0e0', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${uploadProgress}%`, backgroundColor: '#1a73e8', transition: 'width 0.3s' }} />
                </div>
              )}
            </div>
          )}

          {/* Selected preview */}
          {selectedCreative && (
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#d4edda', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <img src={selectedCreative.url} alt="" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '6px' }} />
              <span style={{ color: '#155724', fontWeight: '500', fontSize: '0.9rem' }}>
                {t('createCampaign.selectCreative.selected')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Targeting */}
      {currentStep === 'targeting' && (
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>{t('targeting.title')}</h3>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
              {t('targeting.countries')}
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {[
                ['MX', 'México'], ['CO', 'Colombia'], ['AR', 'Argentina'], ['CL', 'Chile'],
                ['PE', 'Perú'], ['EC', 'Ecuador'], ['BR', 'Brasil'], ['US', 'Estados Unidos'], ['ES', 'España']
              ].map(([code, name]) => (
                <button key={code} onClick={() => {
                  setTargetCountries(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
                }} style={{
                  padding: '0.4rem 0.75rem', borderRadius: '20px', fontSize: '0.85rem', cursor: 'pointer',
                  border: targetCountries.includes(code) ? '2px solid #1a73e8' : '1px solid #ddd',
                  backgroundColor: targetCountries.includes(code) ? '#e8f0fe' : '#fff',
                  color: targetCountries.includes(code) ? '#1a73e8' : '#333'
                }}>
                  {name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
              {t('targeting.ageRange')}
            </label>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#666' }}>{t('targeting.ageMin')}</label>
                <input type="number" min={18} max={65} value={ageMin}
                  onChange={(e) => setAgeMin(Math.max(18, Math.min(65, parseInt(e.target.value) || 18)))}
                  style={{ display: 'block', width: '80px', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.9rem' }} />
              </div>
              <span style={{ marginTop: '1.2rem', color: '#999' }}>—</span>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#666' }}>{t('targeting.ageMax')}</label>
                <input type="number" min={18} max={65} value={ageMax}
                  onChange={(e) => setAgeMax(Math.max(18, Math.min(65, parseInt(e.target.value) || 65)))}
                  style={{ display: 'block', width: '80px', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.9rem' }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Budget */}
      {currentStep === 'budget' && (
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>{t('budget.title')}</h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
              {t('budget.dailyBudget')}
            </label>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <input type="number" min={1} value={dailyBudget}
                onChange={(e) => setDailyBudget(Math.max(1, parseFloat(e.target.value) || 1))}
                style={{ width: '120px', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd', fontSize: '1rem' }} />
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.9rem' }}>
                {['USD', 'MXN', 'COP', 'ARS', 'CLP', 'PEN', 'BRL', 'EUR'].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', fontSize: '0.9rem', color: '#666' }}>
            <p style={{ margin: '0 0 0.25rem 0' }}>
              {currency} {dailyBudget.toFixed(2)} {t('budget.perDay')}
            </p>
            <p style={{ margin: '0 0 0.25rem 0' }}>
              ~{currency} {(dailyBudget * 30).toFixed(2)} {t('budget.perMonth')}
            </p>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#999' }}>{t('budget.minimum')}</p>
          </div>
        </div>
      )}

      {/* Step 5: Review & Publish */}
      {currentStep === 'review' && (
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>{t('review.title')}</h3>

          {/* Campaign name */}
          <div style={{ marginBottom: '1rem' }}>
            <strong style={{ fontSize: '1.1rem' }}>{campaignName || 'Untitled Campaign'}</strong>
          </div>

          {/* Platforms + connection status */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: '500', color: '#666', marginBottom: '0.5rem', display: 'block' }}>
              {t('review.platforms')}
            </label>
            {selectedPlatforms.map(p => {
              const cfg = platformConfigs[p];
              const connected = cfg?.status === 'connected';
              const hasAccount = p === 'meta' ? !!cfg?.selectedAdAccountId : connected;
              return (
                <div key={p} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <span>{PLATFORMS.find(pl => pl.id === p)?.icon}</span>
                  <span style={{ fontSize: '0.9rem' }}>{p.charAt(0).toUpperCase() + p.slice(1)}</span>
                  {connected && hasAccount ? (
                    <span style={{ fontSize: '0.75rem', color: '#155724', backgroundColor: '#d4edda', padding: '1px 6px', borderRadius: '4px' }}>
                      {t('review.checks.connected')}
                    </span>
                  ) : connected && !hasAccount ? (
                    <span style={{ fontSize: '0.75rem', color: '#856404', backgroundColor: '#fff3cd', padding: '1px 6px', borderRadius: '4px' }}>
                      {t('review.checks.noAccount')}
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.75rem', color: '#721c24', backgroundColor: '#f8d7da', padding: '1px 6px', borderRadius: '4px' }}>
                      {t('review.checks.notConnected')}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Copy preview */}
          {generatedContent && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: '500', color: '#666', marginBottom: '0.5rem', display: 'block' }}>
                {t('review.copy')}
              </label>
              {selectedPlatforms.map(p => {
                const c = generatedContent[p];
                if (!c) return null;
                return (
                  <div key={p} style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '6px', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                    <strong>{p.charAt(0).toUpperCase() + p.slice(1)}:</strong> {c.headline} — {c.body}
                  </div>
                );
              })}
            </div>
          )}

          {/* Creative preview */}
          {selectedCreative && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: '500', color: '#666', marginBottom: '0.5rem', display: 'block' }}>
                {t('review.creative')}
              </label>
              <img src={selectedCreative.url} alt="" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }} />
            </div>
          )}

          {/* Targeting summary */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: '500', color: '#666', marginBottom: '0.25rem', display: 'block' }}>
              {t('review.targeting')}
            </label>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              {targetCountries.join(', ') || '—'} | {ageMin}-{ageMax}
            </p>
          </div>

          {/* Budget summary */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: '500', color: '#666', marginBottom: '0.25rem', display: 'block' }}>
              {t('review.budget')}
            </label>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              {currency} {dailyBudget.toFixed(2)} {t('budget.perDay')}
            </p>
          </div>

          {/* Publish result */}
          {publishResult && (
            <div style={{
              padding: '1rem', borderRadius: '8px', marginBottom: '1rem',
              backgroundColor: publishResult.ok ? '#d4edda' : '#f8d7da',
              border: `1px solid ${publishResult.ok ? '#c3e6cb' : '#f5c6cb'}`,
              color: publishResult.ok ? '#155724' : '#721c24'
            }}>
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600' }}>
                {publishResult.ok ? t('review.published') : t('review.error')}
              </p>
              {publishResult.ok && (
                <p style={{ margin: 0, fontSize: '0.85rem' }}>{t('review.publishedPaused')}</p>
              )}
              {publishResult.platformResults && Object.entries(publishResult.platformResults).map(([p, r]) => (
                <p key={p} style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem' }}>
                  {p}: {r.ok ? `✅ ${t('review.platformSuccess')}` : `❌ ${r.error || t('review.platformError')}`}
                </p>
              ))}
              {publishResult.error && !publishResult.platformResults && (
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem' }}>{publishResult.error}</p>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={handlePublish} disabled={publishing || publishResult?.ok}
              style={{
                padding: '0.75rem 1.5rem', backgroundColor: (publishing || publishResult?.ok) ? '#ccc' : '#28a745',
                color: '#fff', border: 'none', borderRadius: '8px',
                cursor: (publishing || publishResult?.ok) ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem', fontWeight: '700', flex: 1
              }}>
              {publishing ? t('review.publishing') : `🚀 ${t('review.publish')}`}
            </button>
            <button onClick={handleSaveDraft} disabled={saving} style={{
              padding: '0.75rem 1.5rem', backgroundColor: '#f0f0f0', color: '#333',
              border: '1px solid #ddd', borderRadius: '8px',
              cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.9rem'
            }}>
              {saving ? '...' : saved ? t('createCampaign.savedDraft') : t('createCampaign.saveDraft')}
            </button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
        <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} style={{
          padding: '0.5rem 1rem', backgroundColor: '#f0f0f0', color: step === 0 ? '#999' : '#333',
          border: '1px solid #ddd', borderRadius: '8px', cursor: step === 0 ? 'not-allowed' : 'pointer', fontSize: '0.85rem'
        }}>
          {t('createCampaign.previous')}
        </button>
        {step < WIZARD_STEPS.length - 1 && (() => {
          const disabled = (step === 0 && !canProceedStep1) || (step === 1 && !canProceedStep2) ||
            (step === 2 && !canProceedStep3) || (step === 3 && !canProceedStep4);
          return (
            <button
              onClick={() => setStep(step + 1)}
              disabled={disabled}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: disabled ? '#ccc' : '#1a73e8',
                color: '#fff', border: 'none', borderRadius: '8px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontSize: '0.85rem', fontWeight: '600'
              }}
            >
              {t('createCampaign.next')}
            </button>
          );
        })()}
      </div>
    </div>
  );
}
